'use client';

import React, { useState } from 'react';
import { useAuth } from 'src/context/auth';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { Drawer, AspAdmin } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import {
  useGetCompanySettingsQuery,
  useUpdateCompanySettingsMutation,
  useCreateCompanySettingsMutation,
  analyticsApi,
  TRENDING_ANALYTICS_TYPE,
} from 'src/api/redux';
import { store } from 'src/redux/store';

type AspValue = { code: string; value: number | string };

const AnalyticsAspDrawer = () => {
  const { user } = useAuth();
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const [aspValues, setAspValues] = useState<AspValue[]>([]);
  const { data: companySettings } = useGetCompanySettingsQuery(undefined, {
    skip: !user?.company?.id,
  });

  const [createCompanySettings, { isLoading: isCreating }] =
    useCreateCompanySettingsMutation();
  const [updateCompanySettings, { isLoading: isUpdating }] =
    useUpdateCompanySettingsMutation();

  const handleClose = () => {
    closeDrawer('asp-drawer');
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleOnSubmit = async () => {
    // Check if company settings for ASP values already exist
    const existingAspSetting = companySettings?.results?.find(
      (setting) => setting.code === 'asp_values'
    );

    // Get existing ASP values to merge with
    let existingAspValues: AspValue[] = [];
    if (existingAspSetting && existingAspSetting.value) {
      try {
        existingAspValues =
          typeof existingAspSetting.value === 'string'
            ? JSON.parse(existingAspSetting.value)
            : existingAspSetting.value;
      } catch (error) {
        console.error('Failed to parse existing ASP values:', error);
      }
    }

    // Create a map of existing values for quick lookup
    const existingMap = new Map(
      existingAspValues.map((item) => [item.code, item.value])
    );

    // Update only the codes that were modified (non-empty values)
    aspValues.forEach((item) => {
      if (item.value !== '') {
        existingMap.set(item.code, Number(item.value));
      }
    });

    // Convert map back to array
    const mergedAspValues = Array.from(existingMap.entries()).map(
      ([code, value]) => ({
        code,
        value: Number(value),
      })
    );

    if (existingAspSetting) {
      // Update existing company settings
      try {
        await updateCompanySettings({
          settingsCode: existingAspSetting.code,
          data: {
            value: JSON.stringify(mergedAspValues),
          },
        }).unwrap();

        // Invalidate ASP analytics cache to trigger refetch
        store.dispatch(
          analyticsApi.util.invalidateTags([
            { type: TRENDING_ANALYTICS_TYPE, id: 'ASP' },
          ])
        );

        handleClose();
      } catch (error) {
        console.error('Failed to update ASP values:', error);
      }
    } else {
      // Create new company settings
      try {
        await createCompanySettings({
          settings: [
            {
              code: 'asp_values',
              value: JSON.stringify(mergedAspValues),
            },
          ],
        }).unwrap();

        // Invalidate ASP analytics cache to trigger refetch
        store.dispatch(
          analyticsApi.util.invalidateTags([
            { type: TRENDING_ANALYTICS_TYPE, id: 'ASP' },
          ])
        );

        handleClose();
      } catch (error) {
        console.error('Failed to submit ASP values:', error);
      }
    }
  };

  return (
    <Drawer
      isOpen={isDrawerOpen('asp-drawer')}
      onClose={handleClose}
      anchor="left"
      size="base2"
      variant="persistent"
      className="z-50 absolute left-0 top-0 bottom-0"
      data-testid="analytics-asp-drawer"
    >
      {/* Drawer Header */}
      <Drawer.Header className="bg-sky-100 text-primary border-b-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p className="font-bold text-lg" data-testid="asp-drawer-title">
              ASP Value Manager
            </p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleCancel}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              data-testid="asp-drawer-close-button"
            />
          </div>
        </div>
      </Drawer.Header>

      {/* Drawer Content */}
      <Drawer.Content className="bg-neutral-050 p-4 flex-1 overflow-auto">
        <div className="w-full h-full flex flex-col gap-4">
          <AspAdmin aspValues={aspValues} setAspValues={setAspValues} />
        </div>
      </Drawer.Content>
      <Drawer.Footer className="px-6 py-4 bg-white border-t border-neutral-200">
        <div className="w-full flex justify-between items-center gap-2">
          <button
            className="w-full rounded-full px-4 py-2 bg-white text-sm font-medium text-primary border border-neutral-200 hover:bg-neutral-100"
            onClick={handleCancel}
            data-testid="asp-drawer-cancel-button"
          >
            Cancel
          </button>
          <button
            className="w-full rounded-full px-4 py-2 bg-sky-600 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleOnSubmit}
            disabled={isCreating || isUpdating}
            data-testid="asp-drawer-apply-button"
          >
            {isCreating || isUpdating ? 'Saving...' : 'Apply'}
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};

export default AnalyticsAspDrawer;
